import { useState } from "react";
import { Check, Search, TriangleAlert, Users, X, type LucideIcon } from "lucide-react";
import { Avatar } from "@/modules/escala/components/Avatar";
import type { CandidatoParticipante } from "@/modules/escala/services/escalaService";

interface PessoaAgrupada {
  pessoaId: string;
  nome: string;
  funcoes: { id: string; nome: string }[];
}

function agruparPorPessoa(candidatos: CandidatoParticipante[]): PessoaAgrupada[] {
  const mapa = new Map<string, PessoaAgrupada>();
  for (const c of candidatos) {
    const atual = mapa.get(c.pessoaId);
    if (atual) {
      atual.funcoes.push({ id: c.funcaoId, nome: c.funcaoNome });
    } else {
      mapa.set(c.pessoaId, { pessoaId: c.pessoaId, nome: c.nome, funcoes: [{ id: c.funcaoId, nome: c.funcaoNome }] });
    }
  }
  return [...mapa.values()].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

/** Busca sem acento e sem diferença de maiúscula/minúscula. */
function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

interface SeletorParticipantesProps {
  /** Integrantes ativos do ministério (uma linha por pessoa+função), já sem quem está na escala. */
  candidatos: CandidatoParticipante[];
  carregando: boolean;
  /** pessoaId → funcaoId escolhida. */
  selecionados: Record<string, string>;
  onMudar: (selecionados: Record<string, string>) => void;
  /** Só existe com uma escala já criada (`escala_verificar_conflito` precisa do id). Aviso não bloqueante. */
  conflitos?: Record<string, boolean>;
  /** Atalho opcional ao lado de "Selecionar todos" (ex.: repetir a equipe da última escala). */
  atalho?: { rotulo: string; icone: LucideIcon; onUsar: () => void };
  /** Limita a altura da lista (uso dentro de modal, que rola por dentro). */
  listaRolavel?: boolean;
}

/**
 * Seletor de pessoas para uma escala — usado no "Adicionar participantes" da
 * escala e na seção "Equipe" da criação (um componente só, G05). Não decide
 * quem pode entrar: a lista vem dos integrantes ativos do ministério e o
 * servidor valida de novo (`escala_adicionar_participantes`).
 */
export function SeletorParticipantes({
  candidatos,
  carregando,
  selecionados,
  onMudar,
  conflitos,
  atalho,
  listaRolavel = false,
}: SeletorParticipantesProps) {
  const [busca, setBusca] = useState("");

  const pessoas = agruparPorPessoa(candidatos);
  const termo = normalizar(busca.trim());
  const visiveis = pessoas.filter((p) => normalizar(p.nome).includes(termo));
  const totalSelecionados = Object.keys(selecionados).length;
  const todosVisiveisMarcados = visiveis.length > 0 && visiveis.every((p) => selecionados[p.pessoaId]);
  const algumConflito = !!conflitos && visiveis.some((p) => conflitos[p.pessoaId]);

  function alternar(pessoa: PessoaAgrupada) {
    const proximo = { ...selecionados };
    if (proximo[pessoa.pessoaId]) delete proximo[pessoa.pessoaId];
    else proximo[pessoa.pessoaId] = pessoa.funcoes[0]!.id;
    onMudar(proximo);
  }

  function alternarTodos() {
    const proximo = { ...selecionados };
    for (const p of visiveis) {
      if (todosVisiveisMarcados) delete proximo[p.pessoaId];
      else if (!proximo[p.pessoaId]) proximo[p.pessoaId] = p.funcoes[0]!.id;
    }
    onMudar(proximo);
  }

  function mudarFuncao(pessoaId: string, funcaoId: string) {
    onMudar({ ...selecionados, [pessoaId]: funcaoId });
  }

  if (carregando) return <p className="text-sm text-muted">Carregando integrantes do ministério...</p>;

  if (pessoas.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-5 py-8 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-muted">
          <Users size={24} aria-hidden />
        </span>
        <p className="text-[15px] font-bold text-text">Ninguém para escalar</p>
        <p className="text-sm text-muted">
          Todos os integrantes ativos deste ministério já participam desta escala (ou o ministério ainda não tem
          integrantes ativos).
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="relative">
        <Search size={18} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          // Dentro do formulário de "Nova escala": Enter na busca não pode criar a escala.
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
          placeholder="Buscar pessoa pelo nome"
          aria-label="Buscar pessoa"
          className="w-full rounded-xl border border-transparent bg-surface-2 py-2.5 pl-10 pr-3.5 text-[15px] text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={alternarTodos}
          disabled={visiveis.length === 0}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2.5 text-[13.5px] font-bold text-text transition-colors hover:bg-primary/10 disabled:opacity-50"
        >
          {todosVisiveisMarcados ? (
            <X size={16} aria-hidden className="text-primary" />
          ) : (
            <Check size={16} aria-hidden className="text-primary" />
          )}
          {todosVisiveisMarcados ? "Limpar seleção" : `Selecionar todos (${visiveis.length})`}
        </button>
        {atalho && (
          <button
            type="button"
            onClick={atalho.onUsar}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2.5 text-left text-[13.5px] font-bold leading-tight text-text transition-colors hover:bg-primary/10"
          >
            <atalho.icone size={16} aria-hidden className="shrink-0 text-primary" />
            {atalho.rotulo}
          </button>
        )}
      </div>

      {totalSelecionados > 0 && (
        <ul aria-label="Pessoas selecionadas" className="flex flex-wrap gap-1.5">
          {pessoas
            .filter((p) => selecionados[p.pessoaId])
            .map((p) => {
              const funcao = p.funcoes.find((f) => f.id === selecionados[p.pessoaId])?.nome ?? "";
              return (
                <li
                  key={p.pessoaId}
                  className="inline-flex min-h-8 items-center gap-1 rounded-full bg-primary/10 pl-3 pr-1.5 text-[13px] font-bold text-text"
                >
                  {p.nome.split(" ")[0]} · {funcao}
                  <button
                    type="button"
                    onClick={() => alternar(p)}
                    aria-label={`Tirar ${p.nome} da seleção`}
                    className="grid h-6 w-6 place-items-center rounded-full transition-colors hover:bg-primary/20"
                  >
                    <X size={14} aria-hidden />
                  </button>
                </li>
              );
            })}
        </ul>
      )}

      {visiveis.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border px-4 py-5 text-center text-sm font-semibold text-text">
          Ninguém com esse nome
        </p>
      ) : (
        <div
          role="group"
          aria-label="Integrantes do ministério"
          className={`flex flex-col overflow-hidden rounded-2xl border border-border bg-surface ${listaRolavel ? "max-h-72 overflow-y-auto" : ""}`}
        >
          {visiveis.map((p) => {
            const marcado = !!selecionados[p.pessoaId];
            const varias = p.funcoes.length > 1;
            const conflito = !!conflitos?.[p.pessoaId];
            return (
              <div
                key={p.pessoaId}
                className={`flex items-center gap-2.5 border-b border-border pr-3 last:border-b-0 ${marcado ? "bg-primary/5" : ""}`}
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={marcado}
                  onClick={() => alternar(p)}
                  className="flex min-h-[60px] min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-left focus:outline-none focus-visible:bg-surface-2"
                >
                  <span
                    aria-hidden
                    className={`grid h-6 w-6 shrink-0 place-items-center rounded-lg border-2 transition-colors ${
                      marcado ? "border-primary bg-primary text-on-primary" : "border-border text-transparent"
                    }`}
                  >
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <Avatar nome={p.nome} tamanho="sm" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="break-words text-[14.5px] font-semibold leading-tight text-text">{p.nome}</span>
                    <span className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted">
                      {varias ? `${p.funcoes.length} funções` : p.funcoes[0]!.nome}
                      {conflito && (
                        <span className="rounded-full bg-warning/20 px-1.5 py-px text-[10.5px] font-extrabold tracking-wide text-text">
                          CONFLITO
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                {varias && (
                  <select
                    value={selecionados[p.pessoaId] ?? p.funcoes[0]!.id}
                    onChange={(e) => mudarFuncao(p.pessoaId, e.target.value)}
                    disabled={!marcado}
                    aria-label={`Função de ${p.nome}`}
                    className="w-[116px] shrink-0 truncate rounded-lg border border-border bg-surface-2 py-1.5 pl-2.5 pr-1 text-[13px] text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  >
                    {p.funcoes.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {algumConflito && (
        <p className="flex items-start gap-1.5 text-[12.5px] text-muted">
          <TriangleAlert size={14} aria-hidden className="mt-px shrink-0 text-warning" />
          <span>
            <b className="text-text">Conflito</b> = já está em outra escala neste horário. É só um aviso: não impede
            de escalar.
          </span>
        </p>
      )}
    </div>
  );
}
